<!-- LazyLoadOnVisible.vue -->
<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

const triggerRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);

let observer: IntersectionObserver | null = null;

onMounted(() => {
    // Guard against the fallback slot being empty or not rendering.
    if (!triggerRef.value) {
        console.warn('Missing trigger');
        return;
    }

    observer = new IntersectionObserver(
        ([entry]) => {
            // Once the fallback placeholder is in the viewport, update the state.
            if (entry.isIntersecting) {
                isVisible.value = true;
                // The observer's job is done, disconnect it to free up resources.
                observer?.disconnect();
            }
        },
        {
            rootMargin: '0px',
        }
    );

    // Start observing the element that holds the fallback content.
    observer.observe(triggerRef.value);
});

// Clean up the observer if the component is removed from the page before it becomes visible.
onUnmounted(() => {
    observer?.disconnect();
});
</script>

<template>
    <!-- Once isVisible is true, this renders the default slot's content -->
    <!-- directly, without any wrapper element. -->
    <slot v-if="isVisible"></slot>

    <!-- Initially, this renders the fallback content inside a div. -->
    <!-- This div is the target for our Intersection Observer. -->
    <!-- It is completely removed from the DOM once the component is visible. -->
    <div v-else ref="triggerRef">
        <slot name="fallback"></slot>
    </div>
</template>
